/**
 * Which Enter practice (account) the user currently has selected.
 *
 * WHY THIS EXISTS (2026-09-15): the selection lived as local React state inside
 * `chat-log.tsx`, mirrored to `localStorage`. Nothing outside that component
 * could see it — so the Tasks view had no idea which practice was selected and
 * queried EVERY connected account, showing a biller the union of four clinics'
 * worklists.
 *
 * This is a MIRROR, not a second source of truth. `chat-log` still owns the
 * picker and still writes `localStorage`; it pushes changes here so other views
 * can react to them. Two writers of one value is how they drift, which is why
 * this store never writes `localStorage` itself.
 *
 * Convention (matches enter-tasks-store): atomic selectors only.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const STORAGE_KEY = 'selectedMcpAccountId';

/** Seed from localStorage so a reload keeps the practice without waiting for chat-log to mount. */
function readPersisted(): string | undefined {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem(STORAGE_KEY) || undefined;
    }
  } catch {
    // Storage can throw in private mode — an unknown selection is not fatal.
  }
  return undefined;
}

interface EnterAccountState {
  selectedAccountId: string | undefined;
  setSelectedAccountId: (id: string | undefined) => void;
}

export const useEnterAccountStore = create<EnterAccountState>()(
  devtools(
    set => ({
      selectedAccountId: readPersisted(),
      setSelectedAccountId: id => set({ selectedAccountId: id }, false, 'setSelectedAccountId'),
    }),
    { name: 'enter-account' }
  )
);

export const useSelectedEnterAccountId = (): string | undefined =>
  useEnterAccountStore(s => s.selectedAccountId);

/** For non-React callers (the picker's persist effect). */
export const setSelectedEnterAccountId = (id: string | undefined): void =>
  useEnterAccountStore.getState().setSelectedAccountId(id);
